import type { PartialWithFieldValue } from 'firebase/firestore'
import { doc } from 'firebase/firestore'
import type { UseDocumentOptions } from 'vuefire'
import converter from './storyConverter'
import type { DSImage3rdParty, DSStory, DisplayImage } from '@/types'
import createService from '@/composables/db/useServiceFactory'

export default function useStoryBase() {
  const db = useFirestore()
  const modelConverter = converter()
  const service = createService<DSStory>('stories', modelConverter)
  const { getResizeUrl } = useStorageHelper()

  const getStoryById = (id: string, once = false, storyRef?: Ref<DSStory>) => {
    const options: UseDocumentOptions<DSStory> = {
      once,
      converter: modelConverter,
      target: storyRef,
    }
    return useDocument<DSStory>(doc(db, 'stories', id), options)
  }

  const getStoryCoverImage = async (story: DSStory) => {
    const coverImage = story.coverImage

    const displayImage: DisplayImage = {
      'url': '',
      'thumbnailUrl': '',
      'alt': '',
      'owner': '',
      'username': '',
      '3rdParty': null,
    }

    if (!coverImage) {
      displayImage.url = getPlaceholderImageUrl(story.id)
      displayImage.thumbnailUrl = displayImage.url
      displayImage.alt = story.topicTitle
      displayImage.owner = story.author
      displayImage.username = story.author
    } else if ((coverImage as DSImage3rdParty)['3rdParty']) {
      const imageObject = coverImage as DSImage3rdParty
      displayImage.url = imageObject.rawUrl
      displayImage.thumbnailUrl = imageObject.thumbnailUrl || imageObject.rawUrl
      displayImage.alt = imageObject.alt
      displayImage.owner = imageObject.owner
      displayImage.username = imageObject.username
      displayImage['3rdParty'] = imageObject['3rdParty']
    } else {
      displayImage.url = await getResizeUrl(coverImage, story.id, story.imageList?.[0].rawUrl)
      displayImage.thumbnailUrl = displayImage.url
      displayImage.alt = story.topicTitle
      displayImage.owner = story.author
      displayImage.username = story.author
    }
    return displayImage
  }

  const incrementLikeCount = (id: string, count: number) => {
    service.incrementField(id, 'likeCount', count)
  }

  return {
    getStoryById,
    getStoryCoverImage,
    deleteStoryById: (id: string) => service.delete(id),
    createStory: (id: string, payload: PartialWithFieldValue<DSStory>) => service.set(id, payload),
    updateStory: (id: string, payload: PartialWithFieldValue<DSStory>) => service.update(id, payload),
    incrementLikeCount,
  }
}
